import React, { useState } from 'react';

import { additionalDocumentUrl, formDownloadUrl, libraryDocumentUrl } from './api';
import AttachDocument from './AttachDocument';
import SubmittedDocumentPreview from './components/SubmittedDocumentPreview';
import LibraryDocumentView from './LibraryDocumentView';
import OfferDecline from './OfferDecline';
import OfferPreview from './OfferPreview';
import OfferSign from './OfferSign';
import PortalDone from './PortalDone';
import PortalExpired from './PortalExpired';
import PortalHome from './PortalHome';
import SmartForm from './SmartForm';
import { usePortalActions } from './usePortalActions';
import { usePortalState } from './usePortalState';
import { useSnackbar } from '../components/SnackbarProvider';

import type { IAnswer } from '@host/components/NewUI/ApprovalForms/types';
import type { IApplicantQuestion } from '@host/components/NewUI/Components/Utilities/QuestionFieldGenerator';

import type { IExpiredBootstrap, IPortalItemRow, IPortalState, TPortalView } from './types';

type TBootstrap = IPortalState | IExpiredBootstrap;

interface Props {
  token: string;
  bootstrap: TBootstrap;
}

const isExpired = (b: TBootstrap): b is IExpiredBootstrap => 'expired' in b && b.expired;

const Portal: React.FC<Props> = ({ token, bootstrap }) => {
  const initialExpired = isExpired(bootstrap);
  const [view, setView] = useState<TPortalView>(initialExpired ? 'expired' : 'home');
  const [selectedItem, setSelectedItem] = useState<IPortalItemRow | null>(null);
  const [formQuestions, setFormQuestions] = useState<IApplicantQuestion[]>([]);
  const [formAnswers, setFormAnswers] = useState<IAnswer[]>([]);
  const [portalError, setPortalError] = useState<string | null>(null);
  const [selectedFormId, setSelectedFormId] = useState<number | null>(null);
  const { showSuccess, showError } = useSnackbar();

  const { data: state } = usePortalState({
    token,
    initialData: initialExpired ? undefined : bootstrap,
    enabled: !initialExpired
  });
  const actions = usePortalActions(token);

  if (view === 'expired' || initialExpired) {
    const exp = isExpired(bootstrap)
      ? bootstrap
      : ({ can_resend: true, offer_title: '' } as IExpiredBootstrap);
    return <PortalExpired canResend={exp.can_resend} offerTitle={exp.offer_title} token={token} />;
  }

  if (!state) return null;

  if (state.declined) {
    return <PortalDone message="The recruiter has been notified." title="Offer declined" />;
  }

  if (view === 'done') {
    return (
      <PortalDone
        message="Thank you for completing your onboarding."
        onBackToPortal={() => setView('home')}
        title="All done!"
      />
    );
  }

  const submittedFiles = selectedItem?.asset_files ?? [];
  const submittedDocument =
    selectedItem?.status === 'submitted' && submittedFiles.length > 0 ? (
      <SubmittedDocumentPreview files={submittedFiles} token={token} />
    ) : null;

  if (view === 'attach' && selectedItem) {
    return (
      <AttachDocument
        item={selectedItem}
        onBack={() => setView('home')}
        submittedDocument={submittedDocument}
        onSubmit={async (files) => {
          await actions.uploadFiles(selectedItem.id, files);
          setView('home');
          showSuccess('Document uploaded');
        }}
      />
    );
  }

  if (view === 'library_document' && selectedItem) {
    const docUrl =
      selectedItem.type === 'document'
        ? additionalDocumentUrl(token, selectedItem.id)
        : libraryDocumentUrl(token, selectedItem.id);
    return (
      <LibraryDocumentView
        documentUrl={docUrl}
        item={selectedItem}
        onBack={() => setView('home')}
        submittedDocument={submittedDocument}
        onComplete={async () => {
          await actions.completeTodo(selectedItem.id);
          setView('home');
          showSuccess('Marked as complete');
        }}
        onSubmit={async (files) => {
          await actions.uploadFiles(selectedItem.id, files);
          setView('home');
          showSuccess('Document uploaded');
        }}
      />
    );
  }

  if (view === 'form' && selectedItem) {
    return (
      <SmartForm
        answerRows={formAnswers}
        downloadUrl={formDownloadUrl(token, selectedItem.id)}
        formId={selectedFormId}
        item={selectedItem}
        loading={actions.isFetchingFormQuestions}
        onBack={() => setView('home')}
        questions={formQuestions}
        onSubmit={async ({ form_id, answers_attributes }) => {
          try {
            await actions.submitForm(selectedItem.id, { form_id, answers_attributes });
            setView('home');
            showSuccess('Form submitted');
          } catch (error) {
            showError(error instanceof Error ? error.message : 'Failed to submit form');
          }
        }}
      />
    );
  }

  if (view === 'preview') {
    return (
      <OfferPreview
        offerTitle={state.offer_title}
        onBack={() => setView('home')}
        onDecline={() => setView('decline')}
        onSign={() => setView('sign')}
        signed={state.stages.sign.complete}
        signLocked={state.stages.sign.locked}
        token={token}
      />
    );
  }

  if (view === 'sign') {
    return (
      <OfferSign
        offerTitle={state.offer_title}
        onBack={() => setView('preview')}
        onConfirm={async (signature) => {
          const next = await actions.signOffer(signature);
          setView(next.all_complete ? 'done' : 'home');
          showSuccess('Offer signed');
        }}
      />
    );
  }

  if (view === 'decline') {
    return (
      <OfferDecline
        onBack={() => setView('preview')}
        onConfirm={async (notes) => {
          await actions.declineOffer(notes);
          setView('done');
        }}
      />
    );
  }

  return (
    <PortalHome
      errorMessage={portalError}
      onNavigate={setView}
      state={state}
      token={token}
      onCompleteTodo={async (item) => {
        try {
          const next = await actions.completeTodo(item.id);
          // Completing the final outstanding item finishes onboarding — show
          // the "All done!" view.
          if (next?.all_complete) setView('done');
          showSuccess('Marked as complete');
        } catch (error) {
          showError(error instanceof Error ? error.message : 'Failed to mark as complete');
        }
      }}
      onSelectItem={async (item) => {
        if (item.type === 'todo') return;
        if (
          (item.type === 'library_document' ||
            (item.type === 'document' && item.item_asset_id != null)) &&
          !item.locked
        ) {
          setPortalError(null);
          setSelectedItem(item);
          setView('library_document');
          return;
        }
        if (item.type === 'smart_form') {
          setPortalError(null);
          try {
            const res = await actions.fetchFormQuestions(item.id);
            setSelectedItem(item);
            setSelectedFormId(res.form_id);
            setFormQuestions(res.fields || []);
            setFormAnswers(res.answers || []);
            setView('form');
          } catch (error) {
            setView('home');
            setSelectedItem(null);
            setSelectedFormId(null);
            setFormQuestions([]);
            setFormAnswers([]);
            setPortalError(error instanceof Error ? error.message : 'Failed to load smart form');
          }
          return;
        }
        setPortalError(null);
        setSelectedItem(item);
        setSelectedFormId(null);
        setFormAnswers([]);
        setView('attach');
      }}
    />
  );
};

export default Portal;
